import React, { useState, useRef, useEffect } from "react";
import classes from "./MedicalInfo.module.css";

const MedicalInfo = ({ onPrevious, onNext, formData, setFormData }) => {
  const [errors, setErrors] = useState({});
  const [allergyList, setAllergyList] = useState([]);
  const [medicationList, setMedicationList] = useState([]);
  const [surgeryList, setSurgeryList] = useState([]);
  
  // Search states
  const [allergySearch, setAllergySearch] = useState("");
  const [showAllergyDropdown, setShowAllergyDropdown] = useState(false);
  const [medicationSearch, setMedicationSearch] = useState("");
  const [showMedicationDropdown, setShowMedicationDropdown] = useState(false);

  // Refs for click outside
  const allergyRef = useRef(null);
  const medicationRef = useRef(null);

  const reactionsList = ["Rash", "Swelling", "Anaphylaxis", "Nausea", "Itching"];
  
  const allergyOptions = ["Penicillin", "Peanuts", "Latex", "Shellfish", "Eggs", "Milk", "Soy", "Wheat", "Sulfa", "Codeine"];
  const medicationOptions = ["Lisinopril", "Metformin", "Levothyroxine", "Amlodipine", "Omeprazole", "Losartan", "Albuterol", "Gabapentin", "Hydrochlorothiazide", "Atorvastatin"];

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (allergyRef.current && !allergyRef.current.contains(event.target)) {
        setShowAllergyDropdown(false);
      }
      if (medicationRef.current && !medicationRef.current.contains(event.target)) {
        setShowMedicationDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleAddAllergy = () => {
    if (!formData.allergen || !formData.reaction) return;
    setAllergyList([...allergyList, { allergen: formData.allergen, reaction: formData.reaction }]);
    setFormData((prev) => ({ ...prev, allergen: "", reaction: "" }));
  };

  const handleAddMedication = () => {
    if (!formData.medicationName || !formData.dosage || !formData.frequency || !formData.route) return;
    setMedicationList([...medicationList, { name: formData.medicationName, dosage: formData.dosage, frequency: formData.frequency, route: formData.route, startDate: formData.medicationStartDate }]);
    setFormData((prev) => ({ ...prev, medicationName: "", dosage: "", frequency: "", route: "", medicationStartDate: "" }));
  };

  const handleAddSurgery = () => {
    if (!formData.procedureName || !formData.surgeryDate) return;
    setSurgeryList([...surgeryList, { procedure: formData.procedureName, date: formData.surgeryDate, hospital: formData.hospital, surgeon: formData.surgeon, notes: formData.surgeryNotes }]);
    setFormData((prev) => ({ ...prev, procedureName: "", surgeryDate: "", hospital: "", surgeon: "", surgeryNotes: "" }));
  };

  const calculateBMI = () => {
    const weight = parseFloat(formData.weight);
    const height = parseFloat(formData.height);
    const heightUnit = formData.heightUnit || "cm";

    if (!weight || !height) return "";

    let heightInMeters;
    if (heightUnit === "cm") heightInMeters = height / 100;
    else if (heightUnit === "m") heightInMeters = height;
    else if (heightUnit === "ft") heightInMeters = height * 0.3048;

    const bmi = weight / (heightInMeters * heightInMeters);
    return bmi.toFixed(1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.bloodGroup) newErrors.bloodGroup = "Blood group is required.";
    if (!formData.genotype) newErrors.genotype = "Genotype is required.";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onNext();
  };

  // Filter allergy options based on search
  const filteredAllergies = allergyOptions.filter(option =>
    option.toLowerCase().includes(allergySearch.toLowerCase())
  );

  // Filter medication options based on search
  const filteredMedications = medicationOptions.filter(option =>
    option.toLowerCase().includes(medicationSearch.toLowerCase())
  );

  // Handle allergy selection
  const handleAllergySelect = (option) => {
    setFormData((prev) => ({ ...prev, knownAllergy: option }));
    setAllergySearch(option);
    setShowAllergyDropdown(false);
  };

  // Handle medication selection
  const handleMedicationSelect = (option) => {
    setFormData((prev) => ({ ...prev, medicationName: option }));
    setMedicationSearch(option);
    setShowMedicationDropdown(false);
  };

  return (
    <div className={classes.container}>
      <form onSubmit={handleSubmit} className={classes.form}>
        
        {/* Vitals */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Vitals</h3>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Blood Group <span className={classes.required}>*</span></label>
              <select name="bloodGroup" className={`${classes.formSelect} ${errors.bloodGroup ? classes.inputError : ""}`} value={formData.bloodGroup || ""} onChange={handleChange}>
                <option value="">Select blood group</option>
                <option>A+</option>
                <option>A-</option>
                <option>B+</option>
                <option>B-</option>
                <option>AB+</option>
                <option>AB-</option>
                <option>O+</option>
                <option>O-</option>
              </select>
              {errors.bloodGroup && <span className={classes.errorText}>{errors.bloodGroup}</span>}
            </div>

            <div className={classes.inputGroup}>
              <label>Genotype <span className={classes.required}>*</span></label>
              <select name="genotype" className={`${classes.formSelect} ${errors.genotype ? classes.inputError : ""}`} value={formData.genotype || ""} onChange={handleChange}>
                <option value="">Select genotype</option>
                <option>AA</option>
                <option>AS</option>
                <option>AC</option>
                <option>SS</option>
                <option>SC</option>
              </select>
              {errors.genotype && <span className={classes.errorText}>{errors.genotype}</span>}
            </div>
          </div>

          {/* HEIGHT - Row 1: Height input | Height Unit */}
          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Height</label>
              <input 
                type="number" 
                name="height" 
                placeholder="Enter height" 
                className={classes.formInput} 
                value={formData.height || ""} 
                onChange={handleChange} 
              />
            </div>

            <div className={classes.inputGroup}>
              <label>Height Unit</label>
              <select name="heightUnit" className={classes.formSelect} value={formData.heightUnit || "cm"} onChange={handleChange}>
                <option>cm</option>
                <option>ft</option>
                <option>m</option>
              </select>
            </div>
          </div>

          {/* WEIGHT - Row 2: Weight input | Weight Unit */}
          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Weight</label>
              <input 
                type="number" 
                name="weight" 
                placeholder="Enter weight" 
                className={classes.formInput} 
                value={formData.weight || ""} 
                onChange={handleChange} 
              />
            </div>

            <div className={classes.inputGroup}>
              <label>Weight Unit</label>
              <select name="weightUnit" className={classes.formSelect} value={formData.weightUnit || "kg"} onChange={handleChange}>
                <option>kg</option>
                <option>lbs</option>
              </select>
            </div>
          </div>

          {/* BMI - Full width */}
          <div className={classes.inputGroup}>
            <label>BMI</label>
            <input type="text" value={calculateBMI()} placeholder="Auto calculated" readOnly className={classes.formInputReadOnly} />
          </div>
        </div>

        {/* Medical Conditions */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Medical Conditions</h3>
          <div className={classes.inputGroup}>
            <label>Existing Medical Conditions</label>
            <select name="existingConditions" className={classes.formSelect} value={formData.existingConditions || ""} onChange={handleChange}>
              <option value="">Select medical condition</option>
              <option>Hypertension</option>
              <option>Diabetes</option>
              <option>Asthma</option>
              <option>Arthritis</option>
            </select>
          </div>
          <label className={classes.checkboxLabel}>
            <input type="checkbox" name="noKnownConditions" checked={formData.noKnownConditions || false} onChange={handleChange} />
            No Known Medical Conditions
          </label>
        </div>

        {/* Allergies */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Allergies</h3>
          
          {/* Searchable Allergy Select */}
          <div className={classes.inputGroup}>
            <label>Known Allergies</label>
            <div className={classes.searchWrapper} ref={allergyRef}>
              <div 
                className={`${classes.searchInputArea} ${showAllergyDropdown ? classes.active : ""}`}
                onClick={() => setShowAllergyDropdown(!showAllergyDropdown)}
              >
                <input
                  type="text"
                  placeholder="Search allergy..."
                  className={classes.searchInput}
                  value={allergySearch}
                  onChange={(e) => {
                    setAllergySearch(e.target.value);
                    setShowAllergyDropdown(true);
                  }}
                  onFocus={() => setShowAllergyDropdown(true)}
                />
                <span className={classes.searchChevron}>▼</span>
              </div>
              {showAllergyDropdown && (
                <div className={classes.searchDropdown}>
                  {filteredAllergies.length > 0 ? (
                    filteredAllergies.map((option) => (
                      <div
                        key={option}
                        className={`${classes.searchOption} ${allergySearch === option ? classes.selected : ""}`}
                        onClick={() => handleAllergySelect(option)}
                      >
                        {option}
                      </div>
                    ))
                  ) : (
                    <div className={classes.noOptions}>No options found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <label className={classes.checkboxLabel}>
            <input type="checkbox" name="noKnownAllergies" checked={formData.noKnownAllergies || false} onChange={handleChange} />
            No Known Allergies
          </label>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Allergen</label>
              <input type="text" name="allergen" placeholder="Enter/select allergen" className={classes.formInput} value={formData.allergen || ""} onChange={handleChange} />
            </div>
            <div className={classes.inputGroup}>
              <label>Reaction</label>
              <select name="reaction" className={classes.formSelect} value={formData.reaction || ""} onChange={handleChange}>
                <option value="">Select reaction</option>
                {reactionsList.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <button type="button" className={classes.addBtn} onClick={handleAddAllergy}>+ Add Allergy</button>
          {allergyList.length > 0 && (
            <div className={classes.listContainer}>
              {allergyList.map((item, idx) => (
                <div key={idx} className={classes.listItem}>
                  <span>{item.allergen}</span>
                  <span className={classes.listMeta}>{item.reaction}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current Medications */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Current Medications</h3>
          <div className={classes.radioGroup}>
            <label>Currently Taking Medication?</label>
            <div className={classes.radioRow}>
              <label className={classes.radioLabel}>
                <input type="radio" name="takingMedication" value="Yes" checked={formData.takingMedication === "Yes"} onChange={handleChange} /> Yes
              </label>
              <label className={classes.radioLabel}>
                <input type="radio" name="takingMedication" value="No" checked={formData.takingMedication === "No"} onChange={handleChange} /> No
              </label>
            </div>
          </div>

          {/* Searchable Medication Select */}
          <div className={classes.inputGroup}>
            <label>Search Medications</label>
            <div className={classes.searchWrapper} ref={medicationRef}>
              <div 
                className={`${classes.searchInputArea} ${showMedicationDropdown ? classes.active : ""}`}
                onClick={() => setShowMedicationDropdown(!showMedicationDropdown)}
              >
                <input
                  type="text"
                  placeholder="Search medication..."
                  className={classes.searchInput}
                  value={medicationSearch}
                  onChange={(e) => {
                    setMedicationSearch(e.target.value);
                    setShowMedicationDropdown(true);
                  }}
                  onFocus={() => setShowMedicationDropdown(true)}
                />
                <span className={classes.searchChevron}>▼</span>
              </div>
              {showMedicationDropdown && (
                <div className={classes.searchDropdown}>
                  {filteredMedications.length > 0 ? (
                    filteredMedications.map((option) => (
                      <div
                        key={option}
                        className={`${classes.searchOption} ${medicationSearch === option ? classes.selected : ""}`}
                        onClick={() => handleMedicationSelect(option)}
                      >
                        {option}
                      </div>
                    ))
                  ) : (
                    <div className={classes.noOptions}>No options found</div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Medication Name <span className={classes.required}>*</span></label>
              <input type="text" name="medicationName" placeholder="Enter medication name" className={classes.formInput} value={formData.medicationName || ""} onChange={handleChange} />
            </div>
            <div className={classes.inputGroup}>
              <label>Dosage <span className={classes.required}>*</span></label>
              <input type="text" name="dosage" placeholder="Enter dosage" className={classes.formInput} value={formData.dosage || ""} onChange={handleChange} />
            </div>
          </div>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Frequency <span className={classes.required}>*</span></label>
              <select name="frequency" className={classes.formSelect} value={formData.frequency || ""} onChange={handleChange}>
                <option value="">Select frequency</option>
                <option>Once daily</option>
                <option>Twice daily</option>
                <option>Three times daily</option>
                <option>As needed</option>
              </select>
            </div>
            <div className={classes.inputGroup}>
              <label>Route <span className={classes.required}>*</span></label>
              <select name="route" className={classes.formSelect} value={formData.route || ""} onChange={handleChange}>
                <option value="">Select route</option>
                <option>Oral</option>
                <option>Intravenous</option>
                <option>Intramuscular</option>
                <option>Topical</option>
              </select>
            </div>
          </div>

          <div className={classes.inputGroup}>
            <label>Start Date</label>
            <input type="date" name="medicationStartDate" className={classes.formInput} value={formData.medicationStartDate || ""} onChange={handleChange} />
          </div>

          <button type="button" className={classes.addBtn} onClick={handleAddMedication}>+ Add Medication</button>
          {medicationList.length > 0 && (
            <div className={classes.listContainer}>
              {medicationList.map((item, idx) => (
                <div key={idx} className={classes.listItem}>
                  <span>{item.name}</span>
                  <span className={classes.listMeta}>{item.dosage} • {item.frequency}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Surgical History */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Surgical History</h3>
          <div className={classes.radioGroup}>
            <label>Previous Surgery?</label>
            <div className={classes.radioRow}>
              <label className={classes.radioLabel}>
                <input type="radio" name="previousSurgery" value="Yes" checked={formData.previousSurgery === "Yes"} onChange={handleChange} /> Yes
              </label>
              <label className={classes.radioLabel}>
                <input type="radio" name="previousSurgery" value="No" checked={formData.previousSurgery === "No"} onChange={handleChange} /> No
              </label>
            </div>
          </div>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Procedure Name <span className={classes.required}>*</span></label>
              <input type="text" name="procedureName" placeholder="Enter procedure" className={classes.formInput} value={formData.procedureName || ""} onChange={handleChange} />
            </div>
            <div className={classes.inputGroup}>
              <label>Date of Surgery <span className={classes.required}>*</span></label>
              <input type="date" name="surgeryDate" className={classes.formInput} value={formData.surgeryDate || ""} onChange={handleChange} />
            </div>
          </div>

          <div className={classes.row2Col}>
            <div className={classes.inputGroup}>
              <label>Hospital / Facility</label>
              <input type="text" name="hospital" placeholder="Enter hospital/facility" className={classes.formInput} value={formData.hospital || ""} onChange={handleChange} />
            </div>
            <div className={classes.inputGroup}>
              <label>Surgeon</label>
              <input type="text" name="surgeon" placeholder="Enter surgeon" className={classes.formInput} value={formData.surgeon || ""} onChange={handleChange} />
            </div>
          </div>

          <div className={classes.inputGroup}>
            <label>Notes</label>
            <textarea name="surgeryNotes" placeholder="Enter additional information..." className={classes.formTextarea} rows="2" value={formData.surgeryNotes || ""} onChange={handleChange} />
          </div>

          <button type="button" className={classes.addBtn} onClick={handleAddSurgery}>+ Add Surgery</button>
          {surgeryList.length > 0 && (
            <div className={classes.listContainer}>
              {surgeryList.map((item, idx) => (
                <div key={idx} className={classes.listItem}>
                  <span>{item.procedure}</span>
                  <span className={classes.listMeta}>{item.date}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Additional Medical Information */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Additional Medical Information</h3>
          <div className={classes.inputGroup}>
            <label>Past Medical History</label>
            <textarea name="pastMedicalHistory" placeholder="Enter relevant past medical history..." className={classes.formTextarea} rows="3" value={formData.pastMedicalHistory || ""} onChange={handleChange} />
          </div>
          <div className={classes.inputGroup}>
            <label>Family Medical History</label>
            <textarea name="familyMedicalHistory" placeholder="Enter relevant family medical history..." className={classes.formTextarea} rows="3" value={formData.familyMedicalHistory || ""} onChange={handleChange} />
          </div>
          <div className={classes.inputGroup}>
            <label>Additional Medical Notes</label>
            <textarea name="additionalMedicalNotes" placeholder="Enter any additional medical information..." className={classes.formTextarea} rows="3" value={formData.additionalMedicalNotes || ""} onChange={handleChange} />
          </div>
        </div>

        {/* Footer */}
        <div className={classes.footer}>
          <div className={classes.note}>
            Medical information helps healthcare professionals provide safe and appropriate care.
          </div>
          <div className={classes.footerButtons}>
            <button type="button" className={classes.previousBtn} onClick={onPrevious}>← Previous</button>
            <button type="submit" className={classes.nextBtn}>Next →</button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MedicalInfo;